import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Check, Info, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Permission, Role } from '../types/api';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { usePermissions } from '../hooks/usePermissions';
import { actionLabel, resourceLabel } from '../lib/permissions';
import { apiService } from '../services/api';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { PropertyList, PropertyRow } from '../components/PropertyList';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { PageHeader } from '../components/PageHeader';
import { DialogActions, DialogForm } from '../components/DialogActions';
import { DataTable, DataTableColumn, hasListError, isListRetrying, listTableProps } from '../components/DataTable';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { useResource } from '../hooks/useResource';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ListEmptyState } from '../components/ListEmptyState';
import { ErrorState } from '../components/ErrorState';
import ConfirmDialog from '../components/ConfirmDialog';
import { sameSet, trimmedText, useRecordChanges, type RecordComparators } from '../hooks/useRecordChanges';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import { Field, FieldGroup } from '../components/Field';
import { classifyError, humanizeError } from '../lib/errors';
import { FormError } from '../components/FormError';
import { EMPTY_VALUE, formatNumber } from '../lib/formatters';
import { routeCopy } from '../routes';

/** h1 and description, from the route registry (DESIGN_SYSTEM.md §6 Naming). */
const PAGE = routeCopy('/my-org/roles');

interface PermissionsGroupProps {
  readOnly: boolean;
  /** "{n} permissions selected|granted", under the matrix. */
  summary: string;
  children: React.ReactNode;
}

/**
 * The permission matrix's group label. Editing, the matrix is a group of
 * controls, so one FieldGroup names it; a fieldset cannot sit inside <tbody>,
 * so the group wraps the whole table rather than each resource row. Read-only
 * it holds marks, not controls, and keeps a plain label.
 */
const PermissionsGroup: React.FC<PermissionsGroupProps> = ({ readOnly, summary, children }) =>
  readOnly ? (
    <div className="space-y-1.5">
      <Label id="role-permissions-label">Permissions</Label>
      {children}
      <p className="text-xs text-muted-foreground">{summary}</p>
    </div>
  ) : (
    <FieldGroup id="role-permissions" legend="Permissions" help={summary}>
      {children}
    </FieldGroup>
  );

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupPermissionsByResource(permissions: Permission[]): Map<string, Permission[]> {
  const map = new Map<string, Permission[]>();
  for (const p of permissions) {
    const group = map.get(p.resource) ?? [];
    group.push(p);
    map.set(p.resource, group);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Role dialog (create / edit)
// ---------------------------------------------------------------------------

/**
 * Mirrors api/src/roles.ts: the slug is stored lowercased, starts with a
 * letter, and is at most 50 characters. The help line says so, and shows the
 * lowercased value before saving rather than rewriting what was typed.
 */
const SLUG_REGEX = /^[a-z][a-z0-9_-]*$/;
const SLUG_MAX_LENGTH = 50;
const SLUG_HELP = 'Optional. Lowercase letters, numbers, hyphens, and underscores, starting with a letter.';

interface RoleDialogProps {
  open: boolean;
  role: Role | null; // null = create mode
  allPermissions: Permission[];
  /** org_roles:write — without it every role opens as values with a Close button. */
  canWrite: boolean;
  /**
   * DELETE /org/{orgId}/roles/{roleId} checks org_roles:write — there is no
   * separate :delete grant for roles — so this is that permission. Only custom
   * roles offer Delete role.
   */
  canDelete: boolean;
  onClose: () => void;
  onSave: (name: string, description: string | null, slug: string | null, permissionIds: string[]) => Promise<void>;
  /** Opens the page's Delete role confirmation for this role. */
  onDeleteRequest: (role: Role) => void;
}

/** A role's form, as the dialog holds it. */
interface RoleFormValues {
  name: string;
  description: string;
  slug: string;
  selectedIds: Set<string>;
}

const ROLE_COMPARATORS: RecordComparators<RoleFormValues> = {
  name: trimmedText,
  description: trimmedText,
  slug: { normalize: (value) => value.trim().toLowerCase() },
  selectedIds: sameSet,
};

const roleFormOf = (role: Role | null): RoleFormValues => ({
  name: role?.name ?? '',
  description: role?.description ?? '',
  slug: role?.slug ?? '',
  selectedIds: new Set(role?.permissions.map((p) => p.id) ?? []),
});

const RoleDialog: React.FC<RoleDialogProps> = ({
  open,
  role,
  allPermissions,
  canWrite,
  canDelete,
  onClose,
  onSave,
  onDeleteRequest,
}) => {
  // Built-in roles are read-only: the API refuses to update them. A reader
  // without org_roles:write gets every role the same way (permissions subtract).
  const isBuiltIn = role?.roleType === 'default';
  const isReadOnly = isBuiltIn || !canWrite;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** What the dialog opened with: the saved role, or the empty create form. */
  const [baseline, setBaseline] = useState<RoleFormValues | null>(null);

  const resetForm = React.useCallback(() => {
    const opened = roleFormOf(role);
    setName(opened.name);
    setDescription(opened.description);
    setSlug(opened.slug);
    setSelectedIds(opened.selectedIds);
    setBaseline(opened);
    setNameError(null);
    setSlugError(null);
    setSubmitError(null);
  }, [role]);

  // Sync state whenever the dialog opens or role changes.
  useEffect(() => {
    if (!open) return;
    resetForm();
  }, [open, resetForm]);

  const form = useMemo(() => ({ name, description, slug, selectedIds }), [name, description, slug, selectedIds]);
  const { isDirty: differs } = useRecordChanges(baseline, form, ROLE_COMPARATORS);
  const isDirty = !isReadOnly && differs;

  // X, Escape, outside click and Cancel all route through one guard, so a
  // half-ticked permission matrix is never dropped silently.
  const guard = useDiscardGuard({
    isDirty,
    isBusy: isSaving,
    onClose,
    onDiscard: resetForm,
    recordNoun: role ? `role "${role.name}"` : 'the new role',
  });

  const canDeleteThisRole = Boolean(role && role.roleType !== 'default' && canDelete);

  // Unsaved edits are discarded (with their own confirmation) before the
  // delete confirmation opens — the confirmation ladder's dirty rule.
  const requestDelete = () => {
    if (!role) return;
    guard.confirmDiscard(() => onDeleteRequest(role));
  };

  const togglePermission = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleResource = (perms: Permission[]) => {
    const allSelected = perms.every((p) => selectedIds.has(p.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        perms.forEach((p) => next.delete(p.id));
      } else {
        perms.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    let valid = true;
    if (!trimmedName) {
      setNameError('Role name is required.');
      valid = false;
    } else {
      setNameError(null);
    }
    const trimmedSlug = slug.trim().toLowerCase() || null;
    if (trimmedSlug !== null && trimmedSlug.length > SLUG_MAX_LENGTH) {
      setSlugError(`Slug must be ${SLUG_MAX_LENGTH} characters or fewer.`);
      valid = false;
    } else if (trimmedSlug !== null && !SLUG_REGEX.test(trimmedSlug)) {
      setSlugError('Slug must start with a letter and contain only lowercase letters, numbers, hyphens, and underscores.');
      valid = false;
    } else {
      setSlugError(null);
    }
    if (!valid) return;

    setIsSaving(true);
    setSubmitError(null);
    try {
      // The FULL body, never a diff. PUT /org/{orgId}/roles/{roleId} is a whole
      // replace: name is required, a missing permissionIds becomes [] (every
      // permission removed) and a missing description becomes null
      // (api/src/roles.ts updateOrgRole).
      await onSave(trimmedName, description.trim() || null, trimmedSlug, Array.from(selectedIds));
      onClose();
    } catch (err: unknown) {
      console.error('Failed to save role:', err);
      // A taken slug or name belongs under that field; anything else above the footer.
      const failure = classifyError(err);
      if (failure.kind === 'conflict' && failure.field === 'slug') {
        setSlugError(humanizeError(err));
      } else if (failure.kind === 'conflict' && failure.field === 'name') {
        setNameError(humanizeError(err));
      } else {
        setSubmitError(humanizeError(err, "Couldn't save the role"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const grouped = useMemo(() => groupPermissionsByResource(allPermissions), [allPermissions]);
  const allActions = useMemo(() => {
    const actions = new Set<string>();
    allPermissions.forEach((p) => actions.add(p.action));
    return Array.from(actions).sort();
  }, [allPermissions]);

  return (
    <>
    <Dialog open={open} pending={isSaving} onOpenChange={guard.onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly ? role?.name : role ? `Edit role "${role.name}"` : 'Create role'}
          </DialogTitle>
          <DialogDescription>
            {isBuiltIn
              ? 'Built-in roles are read-only and cannot be modified.'
              : isReadOnly
                ? 'The permissions this role grants.'
                : "Name the role and pick the permissions it grants."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm
          onSubmit={(event) => {
            event.preventDefault();
            if (!isReadOnly) void handleSave();
          }}
        >
        <DialogBody className="space-y-4">
          {isReadOnly ? (
            // Values, not disabled inputs: nothing here can be changed
            // (DESIGN_SYSTEM.md §5 — an input means you can change it).
            <PropertyList>
              <PropertyRow label="Name">{role?.name}</PropertyRow>
              <PropertyRow label="Description">{role?.description}</PropertyRow>
              <PropertyRow label="Slug" mono>{role?.slug}</PropertyRow>
            </PropertyList>
          ) : (
            <>
          <Field label="Name" error={nameError} required>
            {(control) => (
              <Input
                {...control}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(null);
                }}
                placeholder="e.g. Infrastructure Manager"
                disabled={isSaving}
              />
            )}
          </Field>

          <Field label="Description">
            {(control) => (
              <Textarea
                {...control}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
              />
            )}
          </Field>

          <Field
            label="Slug"
            error={slugError}
            help={
              slug.trim() !== '' && slug.trim().toLowerCase() !== slug ? (
                <>
                  Saved as <span className="font-mono text-foreground">{slug.trim().toLowerCase()}</span>
                </>
              ) : (
                SLUG_HELP
              )
            }
          >
            {(control) => (
              <Input
                {...control}
                mono
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  if (slugError) setSlugError(null);
                }}
                placeholder="e.g. my-custom-role"
                disabled={isSaving}
              />
            )}
          </Field>
            </>
          )}

          {/*
            Guidance, not a warning: the model is deliberately additive and
            unrestricted, and choosing what a role grants is the administrator's
            job. Stating it here is what stops "these two roles overlap" and
            "why could they grant that?" being read as defects.
          */}
          {!isReadOnly && (
            <Alert variant="info">
              <Info aria-hidden="true" />
              <AlertTitle>How permissions combine</AlertTitle>
              <AlertDescription>
                Permissions are additive: a member gets everything all of their roles grant, so two
                roles overlapping is harmless. Anyone who can edit roles can grant any permission
                this organization has — including to themselves — so hand out that ability
                deliberately.
              </AlertDescription>
            </Alert>
          )}

          {/* Permission matrix — sanctioned raw-table-primitive layout */}
          <PermissionsGroup
            readOnly={isReadOnly}
            summary={`${selectedIds.size} permission${selectedIds.size !== 1 ? 's' : ''} ${isReadOnly ? 'granted' : 'selected'}`}
          >
            <div className="rounded-md border">
              <Table aria-labelledby={isReadOnly ? 'role-permissions-label' : 'role-permissions-legend'}>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Resource</TableHead>
                    {allActions.map((action) => (
                      <TableHead key={action} scope="col" className="w-20 text-center">
                        {actionLabel(action)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from(grouped.entries()).map(([resource, perms]) => {
                    const allSelected = perms.every((p) => selectedIds.has(p.id));
                    const someSelected = perms.some((p) => selectedIds.has(p.id));

                    return (
                      <TableRow key={resource}>
                        <TableHead
                          scope="row"
                          className="py-2 text-sm font-medium normal-case tracking-normal text-foreground"
                        >
                          <div className="flex items-center gap-2">
                            {!isReadOnly && (
                              <Checkbox
                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                onCheckedChange={() => toggleResource(perms)}
                                disabled={isSaving}
                                aria-label={`Select all ${resourceLabel(resource)} permissions`}
                              />
                            )}
                            <span>{resourceLabel(resource)}</span>
                          </div>
                        </TableHead>
                        {allActions.map((action) => {
                          const perm = perms.find((p) => p.action === action);
                          return (
                            <TableCell key={action} className="py-2">
                              <div className="flex items-center justify-center">
                                {!perm ? (
                                  // No such permission exists: a blank cell, so it never
                                  // reads as "not granted".
                                  null
                                ) : isReadOnly ? (
                                  selectedIds.has(perm.id) ? (
                                    <>
                                      <Check aria-hidden="true" className="h-4 w-4 text-foreground" />
                                      <span className="sr-only">{`${resourceLabel(resource)}: ${actionLabel(action)} granted`}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span aria-hidden="true" className="text-muted-foreground">{EMPTY_VALUE}</span>
                                      <span className="sr-only">{`${resourceLabel(resource)}: ${actionLabel(action)} not granted`}</span>
                                    </>
                                  )
                                ) : (
                                  <Checkbox
                                    checked={selectedIds.has(perm.id)}
                                    onCheckedChange={() => togglePermission(perm.id)}
                                    disabled={isSaving}
                                    aria-label={`${resourceLabel(resource)}: ${actionLabel(action)}`}
                                  />
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </PermissionsGroup>

          {/* Destroying the record lives in the body, below its fields, never
              in the footer (DESIGN_SYSTEM.md §5, ruling 10). */}
          {canDeleteThisRole && role ? (
            <div className="flex justify-start border-t pt-4">
              <Button type="button" variant="destructive" onClick={requestDelete} disabled={isSaving}>
                <Trash2 aria-hidden="true" />
                Delete role
              </Button>
            </div>
          ) : null}
        </DialogBody>

        {!isReadOnly ? <FormError message={submitError} /> : null}
        {!isReadOnly ? (
          <DialogActions>
            <Button type="button" variant="outline" onClick={guard.requestClose} disabled={isSaving}>
              Cancel
            </Button>
            {/* Edit wakes on a difference; Create role follows validity. */}
            <Button type="submit" loading={isSaving} disabled={Boolean(role) && !isDirty}>
              {role ? 'Save changes' : 'Create role'}
            </Button>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        )}
        </DialogForm>
      </DialogContent>
    </Dialog>

    {guard.guardDialog}
    </>
  );
};

// ---------------------------------------------------------------------------
// Roles page
// ---------------------------------------------------------------------------

const Roles: React.FC = () => {
  const { selectedOrganization, isLoading: orgLoading } = useOrganization();
  const { accessToken } = useUser();
  const { hasPermission } = usePermissions();


  const [dialogRole, setDialogRole] = useState<Role | null | undefined>(undefined); // undefined = closed
  const [deleteRole, setDeleteRole] = useState<Role | null>(null);

  const canWrite = hasPermission('org_roles:write');
  // deleteOrgRole checks org_roles:write (api/src/roles.ts); no roles :delete grant exists.
  const canDelete = canWrite;

  const orgId = selectedOrganization?.id ?? null;

  /*
   * One organization's roles: a bounded set the endpoint returns whole, so it
   * is a memory-source list — it reveals 50 rows at a time through the same
   * count strip and trailing controls, with no network call per batch. It
   * opens sorted by Name, A–Z (the page's chosen default order), and Name is
   * the one sortable column.
   */
  const rolesQuery = useInfiniteList<Role, 'name'>({
    source: 'memory',
    load: ({ signal }) => apiService.listOrgRoles(orgId as string, accessToken as string, signal),
    memory: {
      searchFields: (role) => [role.name, role.slug, role.description],
      sortAccessors: { name: (role) => role.name.toLocaleLowerCase() },
      tiebreak: (role) => role.id,
    },
    rowKey: (role) => role.id,
    enabled: Boolean(orgId && accessToken),
    resetKey: orgId,
    errorContext: 'Failed to load roles',
    initialSort: { column: 'name', direction: 'asc' },
    sortColumns: ['name'],
    // Every full-page list mirrors its query into the URL (DESIGN_SYSTEM §9):
    // a reversed Name sort is written as sort/dir; the default is omitted.
    urlState: true,
  });

  // The permission catalogue the role dialog offers: bounded, not a list.
  const permissionsResource = useResource(
    (signal) => apiService.listOrgPermissions(orgId as string, accessToken as string, signal),
    { enabled: Boolean(orgId && accessToken), resetKey: orgId, deps: [accessToken] },
  );
  const permissions: Permission[] = permissionsResource.data ?? [];

  const handleSave = async (name: string, description: string | null, slug: string | null, permissionIds: string[]) => {
    if (!selectedOrganization?.id || !accessToken) throw new Error('Not authenticated');
    if (dialogRole) {
      const updated = await apiService.updateOrgRole(
        selectedOrganization.id,
        dialogRole.id,
        { name, description, slug, permissionIds },
        accessToken,
      );
      // Patched where it is, marked Updated — never re-sorted, never refetched
      // (a failed background reload would swap the table for an error right
      // after the toast). It moves to its sorted place on the next reset.
      rolesQuery.patchItem(updated.id, () => updated, { mark: 'updated' });
      toast.success('Role updated');
    } else {
      const created = await apiService.createOrgRole(
        selectedOrganization.id,
        { name, description, slug, permissionIds },
        accessToken,
      );
      // Pinned first, marked New, so it never lands past rows not yet revealed.
      rolesQuery.prependItem(created, { mark: 'new' });
      toast.success('Role created');
    }
  };

  const handleDelete = async () => {
    if (!deleteRole || !selectedOrganization?.id || !accessToken) return;
    await apiService.deleteOrgRole(selectedOrganization.id, deleteRole.id, accessToken);
    rolesQuery.removeItem(deleteRole.id);
    // The role's own dialog closes with it.
    setDialogRole(undefined);
    toast.success('Role deleted');
  };

  const columns = useMemo<Array<DataTableColumn<Role>>>(() => {
    const cols: Array<DataTableColumn<Role>> = [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        // Plain text: the row opens through DataTable's Info button (ruling 10).
        render: (role) => <span className="font-medium">{role.name}</span>,
      },
      {
        key: 'slug',
        header: 'Slug',
        render: (role) =>
          role.slug ? (
            <span className="font-mono text-xs text-muted-foreground">{role.slug}</span>
          ) : (
            <span className="text-muted-foreground">{EMPTY_VALUE}</span>
          ),
      },
      {
        key: 'description',
        header: 'Description',
        className: 'text-muted-foreground',
        render: (role) => role.description || EMPTY_VALUE,
      },
      {
        key: 'permissions',
        header: 'Permissions',
        // A count: right-aligned, formatNumber, no decorative icon.
        align: 'right',
        render: (role) => formatNumber(role.permissions.length),
      },
      {
        key: 'type',
        header: 'Type',
        render: (role) =>
          // Both muted attributes: a built-in role is not elevated privilege,
          // and 'system' is reserved for system-administrator scope.
          <StatusBadge status={role.roleType === 'default' ? 'built_in' : 'custom'} />,
      },
    ];
    return cols;
  }, []);

  const listError = hasListError(rolesQuery)
    ? rolesQuery.error
    : permissionsResource.status === 'error'
      ? permissionsResource.error
      : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="shrink-0">
      <PageHeader
        title={PAGE.title}
        description={PAGE.description}
        actions={
          canWrite ? (
            <Button onClick={() => setDialogRole(null)}>
              <Plus aria-hidden="true" />
              Create role
            </Button>
          ) : undefined
        }
      />
      </div>

      {!selectedOrganization && !orgLoading ? (
        <EmptyState
          icon={Building2}
          title="No organization selected"
          description="Select an organization from the switcher to see its roles."
        />
      ) : (
        <DataTable<Role>
          {...listTableProps(rolesQuery)}
          listState={orgLoading ? 'initial' : rolesQuery.status}
          columns={columns}
          rowKey={(role) => role.id}
          listLabel="Roles"
          itemNoun="roles"
          onOpenRow={setDialogRole}
          openLabel={(role) => role.name}
          errorState={
            listError != null ? (
              <ErrorState
                title="Couldn't load roles"
                error={listError}
                onRetry={() => {
                  if (hasListError(rolesQuery)) rolesQuery.reset();
                  if (permissionsResource.status === 'error') void permissionsResource.refetch();
                }}
                isRetrying={isListRetrying(rolesQuery) || permissionsResource.status === 'loading'}
              />
            ) : undefined
          }
          emptyState={
            // The one list empty-state helper (ruling 16). Roles has no search
            // or filter, so only the first-run mode can render.
            <ListEmptyState
              icon={ShieldCheck}
              noun="roles"
              search={rolesQuery.search}
              onClearSearch={rolesQuery.clearSearch}
              filterActive={rolesQuery.hasActiveFilters}
              firstRun={{
                title: 'No roles yet',
                description: canWrite
                  ? 'Create a custom role to control what members can do in this organization.'
                  : 'Roles will appear here once they are created.',
                action: canWrite ? (
                  <Button onClick={() => setDialogRole(null)}>
                    <Plus aria-hidden="true" />
                    Create role
                  </Button>
                ) : undefined,
              }}
            />
          }
        />
      )}

      <RoleDialog
        open={dialogRole !== undefined}
        role={dialogRole ?? null}
        allPermissions={permissions}
        canWrite={canWrite}
        canDelete={canDelete}
        onClose={() => setDialogRole(undefined)}
        onSave={handleSave}
        onDeleteRequest={setDeleteRole}
      />

      <ConfirmDialog
        open={deleteRole !== null}
        title={`Delete role "${deleteRole?.name ?? ''}"?`}
        description="Members who hold it lose the permissions it grants. This can't be undone."
        confirmLabel="Delete role"
        errorContext="Couldn't delete the role"
        onConfirm={handleDelete}
        onClose={() => setDeleteRole(null)}
      />
    </div>
  );
};

export default Roles;
